import { useState, useEffect, useMemo } from 'react'
import { Loader2, RefreshCw, Calculator, UserX, PenLine, CalendarClock, ShieldOff, Trash2, Sparkles } from 'lucide-react'
import { useCurrentMatchday } from '@/hooks/useCurrentMatchday'
import { toast } from '@/hooks/use-toast'
import { invokeEdgeFunction } from '@/lib/edge-functions'
import { supabase } from '@/integrations/supabase/client'
import { TEAM_NAMES, getInformalName, getDisplayName } from '@/lib/teams'
import { useAuth } from '@/context/AuthContext'
import { useQuery, useQueryClient } from '@tanstack/react-query'

interface LeagueToolsProps {
  leagueId: string
  season: string
  competitionId: number
  totalMatchdays: number
  leagueName: string
}

interface LeagueMember {
  user_id: string
  display_name: string
}

export function LeagueTools({ leagueId, season, competitionId, totalMatchdays, leagueName }: LeagueToolsProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { data: currentMatchday, isLoading: isLoadingMatchday } = useCurrentMatchday(season, competitionId)

  // Fixture sync state
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncOddsMatchday, setSyncOddsMatchday] = useState<string>('')

  // Results state
  const [isProcessingResults, setIsProcessingResults] = useState(false)
  const [processMatchday, setProcessMatchday] = useState<string>('')

  // Autopicks state
  const [isProcessingAutopicks, setIsProcessingAutopicks] = useState(false)
  const [autopickMatchday, setAutopickMatchday] = useState<string>('')

  // Override pick state
  const [overrideUserId, setOverrideUserId] = useState<string>('')
  const [overrideMatchday, setOverrideMatchday] = useState<string>('')
  const [overrideTeam, setOverrideTeam] = useState<string>('')
  const [overrideDD, setOverrideDD] = useState(false)
  const [isOverriding, setIsOverriding] = useState(false)
  const [leagueMembers, setLeagueMembers] = useState<LeagueMember[]>([])

  // Fixture exclusion state
  const [exclusionMatchday, setExclusionMatchday] = useState<string>('')
  const [exclusionFixtureId, setExclusionFixtureId] = useState<string>('')
  const [isExcluding, setIsExcluding] = useState(false)
  const [matchdayFixturesForExclusion, setMatchdayFixturesForExclusion] = useState<
    { id: string; home_team: string; away_team: string; kickoff_time: string }[]
  >([])
  const [exclusions, setExclusions] = useState<{ id: string; fixture_id: string; matchday: number; fixture?: { home_team: string; away_team: string } }[]>([])



  // Season management
  const [isUpdatingSeason, setIsUpdatingSeason] = useState(false)
  const seasonOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth()
    const isCalendarYear = /^\d{4}$/.test(season)
    const options: string[] = []
    if (isCalendarYear) {
      const latestYear = currentMonth <= 1 ? currentYear - 1 : currentYear
      for (let y = latestYear + 1; y >= latestYear - 3; y--) options.push(String(y))
    } else {
      const latestStartYear = currentMonth <= 4 ? currentYear - 1 : currentYear
      for (let y = latestStartYear + 1; y >= latestStartYear - 3; y--) {
        options.push(`${y}-${String(y + 1).slice(2)}`)
      }
    }
    if (!options.includes(season)) options.push(season)
    return options
  }, [season])

  // Default matchday selects
  useEffect(() => {
    if (currentMatchday) {
      if (!overrideMatchday) setOverrideMatchday(String(currentMatchday))
      if (!autopickMatchday) setAutopickMatchday(String(currentMatchday))
      if (!syncOddsMatchday) setSyncOddsMatchday(String(currentMatchday))
      if (!processMatchday && currentMatchday > 1) setProcessMatchday(String(currentMatchday - 1))
    }
  }, [currentMatchday])

  // Fetch league members
  useEffect(() => {
    const fetchMembers = async () => {
      const { data } = await supabase
        .from('league_memberships')
        .select('user_id, display_name')
        .eq('league_id', leagueId)
        .eq('status', 'active')
        .order('display_name')
      if (data) setLeagueMembers(data)
    }
    fetchMembers()
  }, [leagueId])

  // Fetch exclusions with fixture details
  useEffect(() => {
    const fetchExclusions = async () => {
      const { data } = await supabase
        .from('fixture_exclusions')
        .select('id, fixture_id, matchday, fixture:fixtures(home_team, away_team)')
        .eq('league_id', leagueId)
        .eq('season', season)
      if (data) {
        setExclusions(data.map(ex => ({
          ...ex,
          fixture: Array.isArray(ex.fixture) ? ex.fixture[0] : ex.fixture ?? undefined,
        })))
      }
    }
    fetchExclusions()
  }, [leagueId, season])

  // Fetch fixtures for exclusion matchday
  useEffect(() => {
    if (!exclusionMatchday || !competitionId) {
      setMatchdayFixturesForExclusion([])
      setExclusionFixtureId('')
      return
    }
    const fetchFixtures = async () => {
      const { data } = await supabase
        .from('fixtures')
        .select('id, home_team, away_team, kickoff_time')
        .eq('season', season)
        .eq('matchday', parseInt(exclusionMatchday, 10))
        .eq('competition_id', competitionId)
        .order('kickoff_time', { ascending: true })
      setMatchdayFixturesForExclusion(data || [])
      setExclusionFixtureId('')
    }
    fetchFixtures()
  }, [exclusionMatchday, season, competitionId])

  // Teams for override picker — competition-specific
  const [competitionTeams, setCompetitionTeams] = useState<{ name: string }[]>([])
  useEffect(() => {
    const fetchTeams = async () => {
      const { data } = await supabase
        .from('teams')
        .select('formal_name')
        .eq('competition_id', competitionId)
        .order('formal_name')
      if (data) setCompetitionTeams(data.map((t) => ({ name: t.formal_name })))
    }
    fetchTeams()
  }, [competitionId])

  const sortedTeams = useMemo(() => {
    if (competitionTeams.length > 0) {
      return competitionTeams
        .map((t) => ({ formal: t.name, informal: getDisplayName(t.name) }))
        .sort((a, b) => a.informal.localeCompare(b.informal))
    }
    return [...TEAM_NAMES]
      .map((t) => ({ formal: t.formal, informal: t.informal }))
      .sort((a, b) => a.informal.localeCompare(b.informal))
  }, [competitionTeams])

  const defaultResultsMatchday = currentMatchday && currentMatchday > 1 ? currentMatchday - 1 : undefined
  const targetMatchday = processMatchday ? parseInt(processMatchday, 10) : defaultResultsMatchday
  const targetAutopickMatchday = autopickMatchday ? parseInt(autopickMatchday, 10) : currentMatchday

  // ── Handlers ──────────────────────────────────────────────

  const handleUpdateLeagueSeason = async (newSeason: string) => {
    try {
      setIsUpdatingSeason(true)
      const { error } = await supabase.from('leagues').update({ season: newSeason }).eq('id', leagueId)
      if (error) throw error
      toast({ title: 'Season updated', description: `League season set to ${newSeason}.` })
      queryClient.invalidateQueries({ queryKey: ['admin-league', leagueId] })
    } catch (e: unknown) {
      toast({ title: 'Update failed', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' })
    } finally {
      setIsUpdatingSeason(false)
    }
  }

  const handleSyncFixtures = async (md: number) => {
    try {
      setIsSyncing(true)
      const { data, error } = await invokeEdgeFunction('fetch-fixtures-with-odds', {
        body: { season, matchday: md, competitionId },
      })
      if (error) throw error
      const total = data?.totalFixtures || 0
      const withOdds = data?.fixturesWithOdds ?? total
      toast({ title: 'Sync complete', description: `MD ${md}: ${total} fixtures, ${withOdds} with odds.` })
    } catch (e: unknown) {
      toast({ title: 'Sync failed', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' })
    } finally {
      setIsSyncing(false)
    }
  }

  const handleProcessResults = async () => {
    if (!targetMatchday) {
      toast({ title: 'No matchday', variant: 'destructive' })
      return
    }
    try {
      setIsProcessingResults(true)
      toast({ title: 'Step 1/2', description: `Populating match results for MD ${targetMatchday}...` })
      const { data: popData, error: popError } = await invokeEdgeFunction('populate-match-results', {
        body: { matchday: targetMatchday, season, leagueId },
      })
      if (popError || popData?.error) {
        toast({ title: 'Populate failed', description: popError?.message || popData?.error, variant: 'destructive' })
        return
      }
      const populated = popData?.populated ?? 0
      if (populated === 0) {
        toast({ title: 'No completed fixtures', description: `No FT fixtures found for MD ${targetMatchday}.`, variant: 'destructive' })
        return
      }
      toast({ title: 'Step 2/2', description: `Processing ${populated} match results...` })
      const { data, error } = await invokeEdgeFunction('process-matchday-selections', {
        body: { matchday: targetMatchday, season, leagueId },
      })
      if (error || data?.error) {
        toast({ title: 'Processing failed', description: error?.message || data?.error, variant: 'destructive' })
        return
      }
      const processed = data?.processed_count ?? 0
      const skipped = data?.skipped?.length ?? 0
      toast({ title: 'Results processed', description: `MD ${targetMatchday}: ${populated} matches, ${processed} scored${skipped > 0 ? `, ${skipped} skipped` : ''}.` })
    } catch (e: unknown) {
      toast({ title: 'Processing error', description: e instanceof Error ? e.message : 'Error', variant: 'destructive' })
    } finally {
      setIsProcessingResults(false)
    }
  }

  const handleProcessAutopicks = async () => {
    if (!targetAutopickMatchday) {
      toast({ title: 'No matchday', variant: 'destructive' })
      return
    }
    try {
      setIsProcessingAutopicks(true)
      const { data, error } = await invokeEdgeFunction('process-autopicks', {
        body: { matchday: targetAutopickMatchday, season, leagueId, force: true },
      })
      if (error || data?.error || !data?.success) {
        toast({ title: 'Autopick failed', description: error?.message || data?.error || data?.message, variant: 'destructive' })
        return
      }
      const assigned = data?.autopicksAssigned ?? 0
      const failed = data?.autopicksFailed ?? 0
      if (assigned === 0 && failed === 0) {
        toast({ title: 'No autopicks needed', description: data?.message || `All members have picks for MD ${targetAutopickMatchday}.` })
      } else {
        toast({ title: 'Autopicks processed', description: `MD ${targetAutopickMatchday}: ${assigned} assigned${failed > 0 ? `, ${failed} failed` : ''}.` })
      }
    } catch (e: unknown) {
      toast({ title: 'Autopick error', description: e instanceof Error ? e.message : 'Error', variant: 'destructive' })
    } finally {
      setIsProcessingAutopicks(false)
    }
  }

  const handleOverridePick = async () => {
    if (!overrideUserId || !overrideMatchday || !overrideTeam) {
      toast({ title: 'Missing fields', variant: 'destructive' })
      return
    }
    const memberName = leagueMembers.find(m => m.user_id === overrideUserId)?.display_name || 'Unknown'
    const teamDisplay = getInformalName(overrideTeam)
    const md = parseInt(overrideMatchday, 10)
    try {
      setIsOverriding(true)
      const { data, error } = await invokeEdgeFunction('admin-override-pick', {
        body: { userId: overrideUserId, leagueId, matchday: md, season, teamName: overrideTeam, usedDoubleDown: overrideDD },
      })
      if (error || data?.error) {
        toast({ title: 'Override failed', description: error?.message || data?.error, variant: 'destructive' })
        return
      }
      toast({ title: `Pick ${data?.action || 'set'}`, description: `${memberName} → ${teamDisplay} for MD${md}${overrideDD ? ' (DD)' : ''}` })
      setOverrideUserId('')
      setOverrideTeam('')
      setOverrideDD(false)
    } catch (e: unknown) {
      toast({ title: 'Override error', description: e instanceof Error ? e.message : 'Error', variant: 'destructive' })
    } finally {
      setIsOverriding(false)
    }
  }

  const handleAddExclusion = async () => {
    if (!exclusionFixtureId || !exclusionMatchday) return
    try {
      setIsExcluding(true)
      const { error } = await supabase.from('fixture_exclusions').insert({
        league_id: leagueId, fixture_id: exclusionFixtureId, season,
        matchday: parseInt(exclusionMatchday, 10), excluded_by: user?.id, reason: 'Rescheduled early fixture',
      })
      if (error) throw error
      toast({ title: 'Fixture excluded' })
      setExclusionFixtureId('')
      const { data } = await supabase.from('fixture_exclusions').select('id, fixture_id, matchday, fixture:fixtures(home_team, away_team)').eq('league_id', leagueId).eq('season', season)
      if (data) setExclusions(data.map(ex => ({ ...ex, fixture: Array.isArray(ex.fixture) ? ex.fixture[0] : ex.fixture ?? undefined })))
      queryClient.invalidateQueries({ queryKey: ['pickable-matchdays'] })
    } catch (e: unknown) {
      toast({ title: 'Exclusion failed', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' })
    } finally {
      setIsExcluding(false)
    }
  }

  const handleRemoveExclusion = async (exclusionId: string) => {
    const { error } = await supabase.from('fixture_exclusions').delete().eq('id', exclusionId)
    if (error) {
      toast({ title: 'Removal failed', description: error.message, variant: 'destructive' })
    } else {
      toast({ title: 'Exclusion removed' })
      const { data } = await supabase.from('fixture_exclusions').select('id, fixture_id, matchday, fixture:fixtures(home_team, away_team)').eq('league_id', leagueId).eq('season', season)
      if (data) setExclusions(data.map(ex => ({ ...ex, fixture: Array.isArray(ex.fixture) ? ex.fixture[0] : ex.fixture ?? undefined })))
      queryClient.invalidateQueries({ queryKey: ['pickable-matchdays'] })
    }
  }

  const inputClass = 'px-2.5 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40'
  const btnClass = 'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 transition-colors'

  return (
    <div className="space-y-8">
      <p className="text-sm text-zinc-500">
        {leagueName} · Matchday {isLoadingMatchday ? '...' : currentMatchday} · {season}
      </p>

      {/* Season Management */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Season Management</h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-zinc-400" />
            <label htmlFor="season-select" className="text-sm text-zinc-500 dark:text-zinc-400">Active Season</label>
            <select id="season-select" value={season} onChange={(e) => handleUpdateLeagueSeason(e.target.value)} disabled={isUpdatingSeason} className={inputClass}>
              {seasonOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {isUpdatingSeason && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
        </div>
      </section>

      {/* Fixture Sync */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Fixture Sync</h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-zinc-500 dark:text-zinc-400">MD</label>
            <select value={syncOddsMatchday} onChange={(e) => setSyncOddsMatchday(e.target.value)} className={`w-24 ${inputClass}`}>
              {Array.from({ length: totalMatchdays }, (_, i) => i + 1).map((md) => (
                <option key={md} value={String(md)}>{md}{md === currentMatchday ? ' (current)' : ''}</option>
              ))}
            </select>
          </div>
          <button onClick={() => handleSyncFixtures(parseInt(syncOddsMatchday, 10) || currentMatchday || 1)} disabled={isSyncing} className={btnClass}>
            {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync MD {syncOddsMatchday || '?'}
          </button>
        </div>
      </section>

      {/* Fixture Exclusions */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Fixture Exclusions</h2>
        {exclusions.length > 0 && (
          <div className="space-y-2">
            {exclusions.map((ex) => {
              return (
                <div key={ex.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-sm">
                  <span className="text-zinc-700 dark:text-zinc-300">MD {ex.matchday}: {ex.fixture ? `${ex.fixture.home_team} vs ${ex.fixture.away_team}` : ex.fixture_id}</span>
                  <button onClick={() => handleRemoveExclusion(ex.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              )
            })}
          </div>
        )}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-zinc-500 dark:text-zinc-400">MD</label>
            <select value={exclusionMatchday} onChange={(e) => setExclusionMatchday(e.target.value)} className={`w-24 ${inputClass}`}>
              <option value="">Select...</option>
              {Array.from({ length: totalMatchdays }, (_, i) => i + 1).map((md) => (
                <option key={md} value={String(md)}>{md}{md === currentMatchday ? ' (current)' : ''}</option>
              ))}
            </select>
          </div>
          {exclusionMatchday && matchdayFixturesForExclusion.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-zinc-500 dark:text-zinc-400">Fixture</label>
              <select value={exclusionFixtureId} onChange={(e) => setExclusionFixtureId(e.target.value)} className={`flex-1 ${inputClass}`}>
                <option value="">Select fixture...</option>
                {matchdayFixturesForExclusion.map((f) => {
                  const alreadyExcluded = exclusions.some((ex) => ex.fixture_id === f.id)
                  const kickoff = new Date(f.kickoff_time)
                  const timeStr = kickoff.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + kickoff.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                  return <option key={f.id} value={f.id} disabled={alreadyExcluded}>{f.home_team} vs {f.away_team} ({timeStr}){alreadyExcluded ? ' [excluded]' : ''}</option>
                })}
              </select>
            </div>
          )}
          <button onClick={handleAddExclusion} disabled={isExcluding || !exclusionFixtureId} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50 transition-colors">
            {isExcluding ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
            Exclude Fixture
          </button>
        </div>
      </section>

      {/* Results Processing */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Results Processing</h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-zinc-500 dark:text-zinc-400">MD</label>
            <select value={processMatchday} onChange={(e) => setProcessMatchday(e.target.value)} className={`w-24 ${inputClass}`}>
              {Array.from({ length: totalMatchdays }, (_, i) => i + 1).map((md) => (
                <option key={md} value={String(md)}>{md}{md === currentMatchday ? ' (current)' : ''}</option>
              ))}
            </select>
          </div>
          <button onClick={handleProcessResults} disabled={isProcessingResults} className={btnClass}>
            {isProcessingResults ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
            Process MD {targetMatchday ?? '?'} Results
          </button>
        </div>
      </section>

      {/* Autopicks */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Autopicks</h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-zinc-500 dark:text-zinc-400">MD</label>
            <select value={autopickMatchday} onChange={(e) => setAutopickMatchday(e.target.value)} className={`w-24 ${inputClass}`}>
              {Array.from({ length: totalMatchdays }, (_, i) => i + 1).map((md) => (
                <option key={md} value={String(md)}>{md}{md === currentMatchday ? ' (current)' : ''}</option>
              ))}
            </select>
          </div>
          <button onClick={handleProcessAutopicks} disabled={isProcessingAutopicks} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50 transition-colors">
            {isProcessingAutopicks ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserX className="h-4 w-4" />}
            Process MD {targetAutopickMatchday ?? '?'} Autopicks
          </button>
        </div>
      </section>

      {/* Override Pick */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Override Pick</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-zinc-500 dark:text-zinc-400 w-16 shrink-0">Player</label>
            <select value={overrideUserId} onChange={(e) => setOverrideUserId(e.target.value)} className={`flex-1 ${inputClass}`}>
              <option value="">Select player...</option>
              {leagueMembers.map((m) => <option key={m.user_id} value={m.user_id}>{m.display_name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-zinc-500 dark:text-zinc-400 w-16 shrink-0">MD</label>
            <select value={overrideMatchday} onChange={(e) => setOverrideMatchday(e.target.value)} className={`w-24 ${inputClass}`}>
              {Array.from({ length: totalMatchdays }, (_, i) => i + 1).map((md) => (
                <option key={md} value={String(md)}>{md}{md === currentMatchday ? ' (current)' : ''}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-zinc-500 dark:text-zinc-400 w-16 shrink-0">Team</label>
            <select value={overrideTeam} onChange={(e) => setOverrideTeam(e.target.value)} className={`flex-1 ${inputClass}`}>
              <option value="">Select team...</option>
              {sortedTeams.map((t) => <option key={t.formal} value={t.formal}>{t.informal}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={overrideDD} onChange={(e) => setOverrideDD(e.target.checked)} className="rounded border-zinc-300 dark:border-zinc-600 text-emerald-500 focus:ring-emerald-500/40" />
              Double Down
            </label>
            <button onClick={handleOverridePick} disabled={isOverriding || !overrideUserId || !overrideMatchday || !overrideTeam} className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-50 transition-colors">
              {isOverriding ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
              Override Pick
            </button>
          </div>
        </div>
      </section>

      {/* AI Season Summary */}
      <SeasonSummarySection leagueId={leagueId} season={season} />
    </div>
  )
}

function SeasonSummarySection({ leagueId, season }: { leagueId: string; season: string }) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const queryClient = useQueryClient()

  const { data: existingSummary, isLoading } = useQuery({
    queryKey: ['ai-summary', leagueId, season],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_summaries')
        .select('id, content, model, created_at')
        .eq('league_id', leagueId)
        .eq('season', season)
        .eq('summary_type', 'season')
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
      if (error?.code === 'PGRST116') return null
      if (error) throw error
      return data
    },
  })

  async function handleGenerate() {
    setIsGenerating(true)
    try {
      const { data, error } = await invokeEdgeFunction<{ summary: { id: string; content: string } }>('generate-season-summary', {
        body: { leagueId, season },
      })
      if (error) throw new Error(error.message)
      toast({ title: 'Season summary generated' })
      queryClient.invalidateQueries({ queryKey: ['ai-summary', leagueId, season] })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to generate summary'
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    } finally {
      setIsGenerating(false)
    }
  }

  async function handleDelete() {
    if (!existingSummary) return
    setIsDeleting(true)
    try {
      const { error } = await supabase
        .from('ai_summaries')
        .delete()
        .eq('id', existingSummary.id)
      if (error) throw error
      toast({ title: 'Summary deleted' })
      queryClient.invalidateQueries({ queryKey: ['ai-summary', leagueId, season] })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to delete summary'
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    } finally {
      setIsDeleting(false)
    }
  }

  const sectionClass = 'rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5'

  return (
    <section className={sectionClass}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-500" />
          <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Season Summary</h3>
        </div>
        <div className="flex items-center gap-2">
          {existingSummary && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                         border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400
                         hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
            >
              {isDeleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Delete
            </button>
          )}
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !!existingSummary}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                       bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
          >
            {isGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {isGenerating ? 'Generating...' : existingSummary ? 'Summary exists' : 'Generate'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        </div>
      ) : existingSummary ? (
        <div className="space-y-2">
          <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap leading-relaxed">
            {existingSummary.content}
          </p>
          <p className="text-xs text-zinc-400 mt-3">
            Generated {new Date(existingSummary.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {existingSummary.model}
          </p>
        </div>
      ) : (
        <p className="text-sm text-zinc-400">
          Generate an AI-powered season wrap-up for this league. Requires AI summaries to be enabled in Platform Settings.
        </p>
      )}
    </section>
  )
}
